import React from 'react';
import { BarChart2, Calculator, Smartphone } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров AR-проекта
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  projectTypes,
  projectScopes
}) => {
  // Обработчик изменения типа проекта
  const handleProjectTypeChange = (typeId) => {
    const selectedType = projectTypes.find(t => t.id === typeId);
    
    setFormData(prev => ({
      ...prev,
      projectType: typeId,
      projectScope: selectedType?.defaultScope || prev.projectScope
    }));
  };
  
  // Обработчик изменения объема проекта
  const handleProjectScopeChange = (scopeId) => {
    setFormData(prev => ({
      ...prev,
      projectScope: scopeId
    }));
  };
  
  // Обработчик изменения количества платформ
  const handlePlatformCountChange = (e) => {
    const numPlatforms = parseInt(e.target.value) || 1;
    setFormData(prev => ({
      ...prev,
      platformCount: String(numPlatforms)
    }));
  };
  
  // Обработчик изменения сроков разработки
  const handleTimelineChange = (e) => {
    const numMonths = parseFloat(e.target.value) || 3;
    setFormData(prev => ({
      ...prev,
      timelineMonths: String(numMonths)
    }));
  };
  
  // Функция расчета
  const calculateCost = () => {
    // Эмулируем нажатие кнопки сабмита в основной форме
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрый подбор параметров
      </h3>
      
      <div className="space-y-5">
        {/* Тип AR-проекта */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Тип AR-проекта</div>
          <div className="grid grid-cols-2 gap-2">
            {projectTypes.slice(0, 4).map(type => (
              <button
                key={type.id}
                type="button"
                onClick={() => handleProjectTypeChange(type.id)}
                className={`p-2 text-sm rounded-lg border transition-colors
                  ${formData.projectType === type.id 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <div className="flex items-center justify-center">
                  <Smartphone className="w-4 h-4 mr-1" />
                  <span>{type.name.split(' ')[0]}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Объем проекта */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Объем проекта</div>
          <div className="grid grid-cols-2 gap-2">
            {projectScopes.map(scope => (
              <button
                key={scope.id}
                type="button"
                onClick={() => handleProjectScopeChange(scope.id)}
                className={`p-2 text-sm rounded-lg border transition-colors
                  ${formData.projectScope === scope.id 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {scope.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Количество платформ */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="platformCountSlider" className="text-gray-600 dark:text-gray-400">Количество платформ</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.platformCount}</span>
          </div>
          <input
            type="range"
            id="platformCountSlider"
            min="1"
            max="10"
            step="1"
            value={formData.platformCount}
            onChange={handlePlatformCountChange}
            aria-label="Количество платформ"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными значениями */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {[1, 2, 3, 5].map(count => (
              <button
                key={count}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, platformCount: count.toString() }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.platformCount === count.toString() 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {count}
              </button>
            ))}
          </div>
        </div>
        
        {/* Срок разработки */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="timelineSlider" className="text-gray-600 dark:text-gray-400">Срок разработки (мес.)</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.timelineMonths}</span>
          </div>
          <input
            type="range"
            id="timelineSlider"
            min="1"
            max="24"
            step="1"
            value={formData.timelineMonths}
            onChange={handleTimelineChange}
            aria-label="Срок разработки (месяцев)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными сроками */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {[1, 3, 6, 12].map(months => (
              <button
                key={months}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, timelineMonths: months.toString() }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.timelineMonths === months.toString() 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {months} {months === 1 ? 'мес' : 'мес'}
              </button>
            ))}
          </div>
        </div>
        
        {/* Дополнительные опции */}
        <div className="space-y-2">
          <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              name="includeBackend"
              checked={formData.includeBackend}
              onChange={(e) => setFormData(prev => ({ ...prev, includeBackend: e.target.checked }))}
              className="form-checkbox mr-2"
            />
            <span>
              Бэкенд и серверная часть
            </span>
          </label>
          
          <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              name="includeMaintenancePlan"
              checked={formData.includeMaintenancePlan}
              onChange={(e) => setFormData(prev => ({ ...prev, includeMaintenancePlan: e.target.checked }))}
              className="form-checkbox mr-2"
            />
            <span>
              План поддержки
            </span>
          </label>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateCost}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать стоимость
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;